import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  LoginResponse,
  OrganizationDataDto,
  OrganizationResponseDto,
  PatientResponseDto,
} from "models/scriptSure";
import { ScriptSureAllergyRequest } from "models/scriptSure/scriptSureAllergyRequest";
import { ScriptSureAllergyResponse } from "models/scriptSure/scriptSureAllergyResponse";
import { ScriptSureDrug } from "models/scriptSure/scriptsureDrug";
import { ScriptSureDrugDoseResponse } from "models/scriptSure/scriptsureDrugDoseResponse";
import { ScriptSureDrugsRequest } from "models/scriptSure/scriptsureDrugsRequest";
import { ScriptSureDrugsResponse } from "models/scriptSure/scriptsureDrugsResponse";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import { tap } from "rxjs/operators";
import { mockAllergySearch, mockMedicationSearch } from "./mock-config";
import { mockMedDoses } from "./mock-med-doses";
import { mockGetOrganization } from "./mock-organization";
import { mockGetPatientByMRN } from "./mock-patient";

@Injectable()
export class MedicationDataService {
  private apiUrl: string;
  private mock: boolean = false;

  // State
  private organization: OrganizationDataDto;
  private organization$: Observable<OrganizationDataDto>;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    public stateData: StateManagementService
  ) {
    const config = this.configurationService.getConfig();
    this.apiUrl = config.apiUrl;
    if (!config.scriptSureEnabled) {
      this.mock = true;
    }
    if (config.isLocal && config.localEndpoints.scriptSure) {
      this.apiUrl = config.localEndpoints.scriptSure;
    } else {
      this.apiUrl = config.apiUrl + "/scriptsure";
    }
    // Preload organization
    this.stateData.showEPrescribe.subscribe((value) => {
      if (value) {
        this.getOrganization().subscribe(
          () => {},
          (err) => {
            if (err.status === 404) {
              console.info(
                "Tenant not configured for ePrescribe. This is not an error."
              );
              return;
            }
            throw err;
          }
        );
      }
    });
  }

  public getMedications(
    scriptureRequest: ScriptSureDrugsRequest
  ): Observable<ScriptSureDrugsResponse> {
    if (this.mock) {
      return of(mockMedicationSearch);
    }
    return this.http
      .post<ScriptSureDrugsResponse>(
        `${this.apiUrl}/proxy/druglist/search`,
        scriptureRequest
      )
      .mergeMap(this.groupMedications);
  }

  public searchAllergies(
    scriptureRequest: ScriptSureAllergyRequest
  ): Observable<ScriptSureAllergyResponse> {
    if (this.mock) {
      return of(mockAllergySearch);
    }
    return this.http.post<ScriptSureAllergyResponse>(
      `${this.apiUrl}/proxy/allergy/search`,
      scriptureRequest
    );
  }

  private groupMedications(
    response: ScriptSureDrugsResponse
  ): Observable<ScriptSureDrugsResponse> {
    const drugs = Object.values(
      response.drugs.reduce<{ [key: number]: ScriptSureDrug }>((acc, val) => {
        return {
          ...acc,
          [val.ROUTED_MED_ID]: val,
        };
      }, {})
    ).sort((a, b) => {
      const aDescr = a.MED_ROUTED_MED_ID_DESC.toLocaleUpperCase();
      const bDescr = b.MED_ROUTED_MED_ID_DESC.toLocaleUpperCase();
      if (aDescr < bDescr) {
        return -1;
      }
      if (aDescr > bDescr) {
        return 1;
      }
      return 0;
    });
    drugs.forEach((drug) => delete drug.DrugGroup);
    return of({
      drugs,
    });
  }

  public getMedicationDoses(
    routedMedId: number
  ): Observable<ScriptSureDrugDoseResponse[]> {
    if (this.mock) {
      return of(mockMedDoses);
    }
    return this.http.get<ScriptSureDrugDoseResponse[]>(
      `${this.apiUrl}/proxy/fdb/drug/doses/${routedMedId}`
    );
  }

  public getPatientByMRN(mrn: string): Observable<PatientResponseDto> {
    if (this.mock) {
      return of(mockGetPatientByMRN(mrn));
    }
    return this.http.get<PatientResponseDto>(`${this.apiUrl}/patient/${mrn}`);
  }

  public getOrganization(): Observable<OrganizationResponseDto> {
    if (this.organization) {
      // Already loaded (cached)
      return of(this.organization);
    }
    if (this.mock) {
      // Mocked
      this.organization = mockGetOrganization();
      return of(mockGetOrganization());
    }
    // Request not yet made
    return (this.organization$ = this.http
      .get<OrganizationResponseDto>(`${this.apiUrl}/organization`)
      .pipe(tap((organization) => (this.organization = organization))));
  }

  public getLogin(): Observable<LoginResponse> {
    if (this.mock) {
      return of({ sessionToken: "abc" });
    }
    return this.http.get<LoginResponse>(`${this.apiUrl}/get-login`);
  }
}



// WEBPACK FOOTER //
// ./src/providers/order-data-service/medications-data-service.ts